import { ReactElement } from 'react'
import { Tooltip as MuiTooltip, TooltipProps as LibTooltipProps } from '@mui/material'
import isUndefined from 'lodash/isUndefined'

import './tooltip.scss'

export interface TooltipProps {
    children: ReactElement<any>
    tooltipData?: {
        imageSrc?: string
        text?: string
        additionalText?: string
    }
    placement?: LibTooltipProps['placement']
}

const ENTER_DELAY = 300
const LEAVE_DELAY = 500

/**
 * Custom tooltip component that displays text and image as a tooltip
 *
 * Note: keep the actual image width as 200 px to prevent tooltip jump on show/hide
 * @param children
 * @param tooltipData
 * @param placement
 * @param isOpen
 * @constructor
 */
const Tooltip = ({ children, tooltipData, placement = 'bottom' }: TooltipProps) => {
    const handleTooltipTitle = (tooltipData: TooltipProps['tooltipData']) => (
        <div className="tooltip-content-wrapper">
            {tooltipData?.imageSrc && (
                <img
                    alt="tooltip image"
                    className="tooltip-image"
                    data-testid="tooltip-image"
                    src={tooltipData.imageSrc}
                    width={200}
                />
            )}

            <div className="tooltip-text-wrapper">
                {tooltipData?.text && (
                    <p className="tooltip-text" data-testid="tooltip-text">
                        {tooltipData.text}
                    </p>
                )}

                {tooltipData?.text && tooltipData?.additionalText && (
                    <p className="tooltip-additional-text" data-testid="tooltip-additional-text">
                        {tooltipData?.additionalText}
                    </p>
                )}
            </div>
        </div>
    )

    if (isUndefined(tooltipData?.imageSrc) && isUndefined(tooltipData?.text)) {
        return children
    }

    return (
        <MuiTooltip
            arrow
            classes={{ popper: 'custom-tooltip' }}
            data-testid="custom-tooltip"
            enterDelay={ENTER_DELAY}
            enterNextDelay={LEAVE_DELAY}
            leaveDelay={LEAVE_DELAY}
            placement={placement}
            title={handleTooltipTitle(tooltipData)}
        >
            {children}
        </MuiTooltip>
    )
}

export default Tooltip
